import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/Textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, X } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { supabase } from "@/lib/supabase";
import { runInventoryTransaction } from "@/lib/api/inventoryTransactions";


export function CancellationDialog({ 
  isOpen, 
  onClose, 
  order, 
  onOrderCancelled 
}) {
  const [cancellationReason, setCancellationReason] = useState("");
  const [cancellationNotes, setCancellationNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const isPartiallyServedOrder = order?.status === "partially_served";

  useEffect(() => {
    if (!isOpen) return;
    setCancellationNotes("");
    setCancellationReason(isPartiallyServedOrder ? "partial_served_not_manage" : "");
  }, [isOpen, isPartiallyServedOrder]);
  const copyTextToClipboard = async (text) => {
    if (!text) return false;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (_) {
      // Fallback below.
    }

    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(textarea);
      return copied;
    } catch (_) {
      return false;
    }
  };

  const handleCancel = () => {
    setCancellationReason("");
    setCancellationNotes("");
    onClose();
  };

  const handleConfirmCancellation = async () => {
    if (!cancellationReason) {
      toast({
        title: "Error",
        description: "Debe seleccionar un motivo de cancelación",
        variant: "destructive",
      });
      return;
    }

    if (isPartiallyServedOrder && cancellationReason !== "partial_served_not_manage") {
      toast({
        title: "Motivo no permitido",
        description: "En pedidos parcialmente servidos solo se permite: Parcialmente servido y no se va a gestionar.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const result = await runInventoryTransaction('cancel_order_transaction', order.id, {
        p_order_id: order.id,
        p_reason: cancellationReason,
        p_notes: cancellationNotes,
      });
      toast({
        title: 'Pedido cancelado',
        description: result.recoveredPreviousOperation
          ? 'Se ha confirmado la cancelación anterior.'
          : `El pedido #${order.number} conserva su historial y se han anulado las unidades pendientes.`,
      });

      // Llamar a la función de callback para actualizar la lista
      if (onOrderCancelled) {
        onOrderCancelled();
      }

      handleCancel();

    } catch (error) {
      console.error('Error cancelling order:', error);
      const messageParts = [
        error?.message,
        error?.details,
        error?.hint
      ].filter(Boolean);
      const errorText = messageParts[0]
        ? `No se pudo cancelar el pedido. ${messageParts.join(" | ")}`
        : "No se pudo cancelar el pedido. Inténtelo de nuevo.";
      toast({
        title: "Error",
        description: errorText,
        variant: "destructive",
        duration: 2147483647,
        autoDismiss: false,
        action: (
          <ToastAction
            altText="Copiar error"
            onClick={async () => {
              const copied = await copyTextToClipboard(errorText);
              toast({
                title: copied ? "Error copiado" : "No se pudo copiar",
                description: copied
                  ? "El detalle del error se ha copiado al portapapeles."
                  : "No se pudo copiar automáticamente. Puede copiar el texto del mensaje.",
                variant: copied ? "default" : "destructive",
              });
            }}
          >
            Copiar error
          </ToastAction>
        ),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getReasonLabel = (reason) => {
    const reasons = {
      'error': 'Error al introducir el pedido',
      'client_cancellation': 'Anulación expresa del cliente',
      'partial_served_not_manage': 'Parcialmente servido y no se va a gestionar'
    };
    return reasons[reason] || reason;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Cancelar Pedido #{order?.number}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">Atención:</p>
                <p>Esta acción conserva las líneas ya servidas y anula solo las líneas pendientes. El inventario actualizará únicamente lo pendiente y se guardará trazabilidad en historial.</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="cancellation-reason">Motivo de cancelación *</Label>
            <Select
              value={cancellationReason}
              onValueChange={setCancellationReason}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar motivo" />
              </SelectTrigger>
              <SelectContent>
                {!isPartiallyServedOrder && (
                  <SelectItem value="error">Error al introducir el pedido</SelectItem>
                )}
                {!isPartiallyServedOrder && (
                  <SelectItem value="client_cancellation">Anulación expresa del cliente</SelectItem>
                )}
                <SelectItem value="partial_served_not_manage">Parcialmente servido y no se va a gestionar</SelectItem>
              </SelectContent>
            </Select>
            {isPartiallyServedOrder && (
              <p className="text-xs text-amber-700">
                Pedido parcialmente servido: el único motivo permitido es "Parcialmente servido y no se va a gestionar".
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="cancellation-notes">Notas adicionales (opcional)</Label>
            <Textarea
              id="cancellation-notes"
              placeholder="Describa detalles adicionales sobre la cancelación..."
              value={cancellationNotes}
              onChange={(e) => setCancellationNotes(e.target.value)}
              rows={3}
            />
          </div>

          {cancellationReason && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <span className="font-medium">Motivo seleccionado:</span> {getReasonLabel(cancellationReason)}
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmCancellation}
            disabled={!cancellationReason || isLoading}
            className="bg-red-600 hover:bg-red-700"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Cancelando...
              </>
            ) : (
              <>
                <AlertTriangle className="h-4 w-4 mr-2" />
                Confirmar Cancelación
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 
